/**
 * Food Truck Resources — category-first provider directory.
 * Handles /resources (directory) and /resources/:slug (category OR provider
 * detail via backend resolve). Coming Soon mode gates everything for the
 * public; admins get a private "Preview Live Directory" toggle.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import api from "../lib/api";
import { GlBasemap } from "../lib/basemap";
import { Button } from "../components/ui/button";
import PageMeta from "../components/PageMeta";
import {
  Toolbox, MagnifyingGlass, MapPin, CircleNotch, X, Phone, Globe, EnvelopeSimple,
  Truck, ForkKnife, Wrench, Broom, FireExtinguisher, Flame, ShieldCheck, Bank,
  ShoppingCart, CookingPot, PaintBrush, TShirt, DeviceMobile, Lightning, Gear,
  Recycle, CalendarCheck, Megaphone, Files, ArrowLeft, Star, SealCheck, NavigationArrow,
} from "@phosphor-icons/react";

const ICONS = { Truck, ForkKnife, Wrench, Broom, FireExtinguisher, Flame, ShieldCheck, Bank, ShoppingCart, CookingPot, PaintBrush, TShirt, DeviceMobile, Lightning, Gear, Recycle, CalendarCheck, Megaphone, Files, Toolbox };
const CatIcon = ({ name, className }) => {
  const I = ICONS[name] || Toolbox;
  return <I weight="duotone" className={className} />;
};

const track = (event_type, extra = {}) => api.post("/resources/track", { event_type, ...extra }).catch(() => {});

const useNoIndex = (on) => {
  useEffect(() => {
    if (!on) return;
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, [on]);
};

const LEVEL_BADGE = {
  featured: ["Featured Partner", "bg-amber-100 text-amber-800"],
  category_sponsor: ["Sponsor", "bg-violet-100 text-violet-800"],
  regional_sponsor: ["Sponsor", "bg-violet-100 text-violet-800"],
  presenting_sponsor: ["Presenting Sponsor", "bg-primary/10 text-primary"],
};

const pinIcon = (featured) => L.divIcon({
  className: "",
  html: `<div style="width:${featured ? 30 : 24}px;height:${featured ? 30 : 24}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${featured ? "#7c3aed" : "#ea580c"};border:2.5px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35)"></div>`,
  iconSize: [30, 30], iconAnchor: [15, 28],
});

const clusterIcon = (n) => L.divIcon({
  className: "",
  html: `<div style="width:34px;height:34px;border-radius:50%;background:#ea580c;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-size:13px;font-family:sans-serif">${n}</div>`,
  iconSize: [34, 34], iconAnchor: [17, 17],
});

/* Centers the map on the provider selected from the directory list. */
const FlyToSelected = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target && target.lat != null) map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), 11), { duration: 0.6 });
  }, [target, map]);
  return null;
};

/* ---------------- modals ---------------- */

const FormModal = ({ title, fields, submitLabel, onSubmit, onClose, testid }) => {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-[1200] bg-black/50 grid place-items-center p-4" data-testid={testid}>
      <div className="bg-card rounded-2xl max-w-lg w-full p-6 max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose} data-testid={`${testid}-close`}><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 space-y-3">
          {fields.map((f) => (
            <label key={f.key} className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{f.label}{f.required && " *"}</span>
              {f.type === "textarea" ? (
                <textarea rows={3} value={vals[f.key] || ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm" data-testid={`${testid}-${f.key}`} />
              ) : f.type === "select" ? (
                <select value={vals[f.key] || f.options[0]} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} className="mt-1 w-full h-10 rounded-lg border bg-background px-3 text-sm" data-testid={`${testid}-${f.key}`}>
                  {f.options.map((o) => <option key={o}>{o}</option>)}
                </select>
              ) : (
                <input value={vals[f.key] || ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} className="mt-1 w-full h-10 rounded-lg border bg-background px-3 text-sm" data-testid={`${testid}-${f.key}`} />
              )}
            </label>
          ))}
        </div>
        <Button
          className="mt-5 w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          disabled={busy || fields.some((f) => f.required && !(vals[f.key] || "").trim())}
          data-testid={`${testid}-submit`}
          onClick={async () => { setBusy(true); try { await onSubmit(vals); onClose(); } finally { setBusy(false); } }}
        >
          {busy ? <CircleNotch className="h-4 w-4 animate-spin" /> : submitLabel}
        </Button>
      </div>
    </div>
  );
};

export const SuggestModal = ({ onClose }) => (
  <FormModal
    title="Suggest a Resource" testid="resource-suggest-modal" submitLabel="Submit suggestion" onClose={onClose}
    fields={[
      { key: "business_name", label: "Business name", required: true },
      { key: "category", label: "Category" },
      { key: "city_state", label: "City & state" },
      { key: "website", label: "Website" },
      { key: "phone", label: "Phone (if known)" },
      { key: "reason", label: "Why do you recommend them?", type: "textarea" },
      { key: "submitter_name", label: "Your name" },
      { key: "submitter_email", label: "Your email" },
    ]}
    onSubmit={async (v) => { await api.post("/resources/suggest", v); toast.success("Thanks! Our team will review your suggestion."); }}
  />
);

export const PartnerModal = ({ onClose }) => (
  <FormModal
    title="Become a TruckTrackr Partner" testid="resource-partner-modal" submitLabel="Send inquiry" onClose={onClose}
    fields={[
      { key: "business_name", label: "Business name", required: true },
      { key: "contact_name", label: "Contact name", required: true },
      { key: "email", label: "Email", required: true },
      { key: "phone", label: "Phone" },
      { key: "website", label: "Website" },
      { key: "category", label: "Business category" },
      { key: "physical_location", label: "Physical location" },
      { key: "service_area", label: "Service area" },
      { key: "services", label: "Services offered", type: "textarea" },
      { key: "sponsorship_interest", label: "Sponsorship interest", type: "select", options: ["Standard Directory Listing", "Featured Partner", "Category Sponsor", "Regional Sponsor", "Presenting Sponsor", "Not Sure\u2014Tell Me More"] },
      { key: "message", label: "Additional message", type: "textarea" },
    ]}
    onSubmit={async (v) => { await api.post("/resources/partner-inquiry", v); toast.success("Inquiry sent — we'll be in touch!"); }}
  />
);

/* ---------------- provider detail ---------------- */

function ProviderDetail({ provider, indexable }) {
  useNoIndex(!indexable);
  const [showReport, setShowReport] = useState(false);
  useEffect(() => { track("detail_view", { provider_id: provider.id }); }, [provider.id]);
  const p = provider;
  const [lvlLabel, lvlCls] = LEVEL_BADGE[p.listing_level] || [];
  const addr = p.address || {};
  const addrText = [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(", ");
  return (
    <div className="mx-auto max-w-4xl px-4 py-6" data-testid="resource-detail-page">
      <PageMeta title={`${p.name} — Food Truck Resources | TruckTrackr`} description={p.short_description} canonical={`https://thetrucktrackr.com/resources/${p.slug}`} />
      <Link to="/resources" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All resources</Link>
      {p.cover_url && <img src={p.cover_url} alt={`${p.name} cover`} className="mt-4 w-full h-48 sm:h-64 object-cover rounded-2xl" />}
      <div className="mt-4 flex items-start gap-4">
        {p.logo_url && <img src={p.logo_url} alt={`${p.name} logo`} className="h-16 w-16 rounded-xl object-cover border" />}
        <div className="flex-1">
          <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">{p.name}</h1>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {lvlLabel && <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${lvlCls}`}>{lvlLabel}</span>}
            {(p.badges || []).map((b) => <span key={b} className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-green-100 text-green-800 inline-flex items-center gap-1"><SealCheck weight="fill" className="h-3 w-3" />{b}</span>)}
            {p.category_names.filter(Boolean).map((c) => <span key={c} className="text-[11px] font-medium rounded-full px-2 py-0.5 bg-muted text-muted-foreground">{c}</span>)}
          </div>
        </div>
      </div>
      {p.offer && (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="resource-offer">
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1"><Star weight="fill" className="h-3.5 w-3.5" /> TruckTrackr offer</div>
          <div className="text-sm font-semibold text-amber-900 mt-0.5">{p.offer}</div>
        </div>
      )}
      <p className="mt-4 text-[15px] leading-relaxed whitespace-pre-line">{p.description}</p>
      {(p.services || []).length > 0 && (
        <div className="mt-4"><div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Services</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">{p.services.map((s) => <span key={s} className="text-xs rounded-full border px-2.5 py-1">{s}</span>)}</div></div>
      )}
      <div className="mt-5 flex flex-wrap gap-2">
        {p.phone && <Button asChild variant="outline" className="rounded-full gap-1.5" data-testid="resource-phone-btn"><a href={`tel:${p.phone}`} onClick={() => track("phone_click", { provider_id: p.id })}><Phone className="h-4 w-4" />{p.phone}</a></Button>}
        {p.website && <Button asChild className="rounded-full gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="resource-website-btn"><a href={p.website} target="_blank" rel="noopener noreferrer" onClick={() => track("website_click", { provider_id: p.id })}><Globe className="h-4 w-4" />Visit website</a></Button>}
        {p.email && <Button asChild variant="outline" className="rounded-full gap-1.5" data-testid="resource-email-btn"><a href={`mailto:${p.email}`} onClick={() => track("contact_click", { provider_id: p.id })}><EnvelopeSimple className="h-4 w-4" />Email</a></Button>}
        {addrText && <Button asChild variant="outline" className="rounded-full gap-1.5" data-testid="resource-directions-btn"><a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addrText)}`} target="_blank" rel="noopener noreferrer" onClick={() => track("directions_click", { provider_id: p.id })}><NavigationArrow className="h-4 w-4" />Get directions</a></Button>}
      </div>
      <div className="mt-4 text-sm text-muted-foreground space-y-1">
        {addrText && <div data-testid="resource-address">{addrText}</div>}
        {p.service_area?.label && <div data-testid="resource-service-area">Service area: {p.service_area.label}{p.service_area.radius_mi ? ` (~${p.service_area.radius_mi} mi)` : ""}</div>}
        {p.hours && <div>Hours: {p.hours}</div>}
        {Object.entries(p.socials || {}).filter(([, v]) => v).map(([k, v]) => <div key={k}><a className="text-primary hover:underline" href={v} target="_blank" rel="noopener noreferrer">{k}</a></div>)}
      </div>
      {p.lat != null && (
        <div className="mt-5 rounded-2xl overflow-hidden border h-64" data-testid="resource-detail-map">
          <MapContainer center={[p.lat, p.lng]} zoom={p.approx_pin ? 10 : 13} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
            <GlBasemap />
            <Marker position={[p.lat, p.lng]} icon={pinIcon(p.sponsor_active && p.listing_level !== "standard")} />
          </MapContainer>
          {p.approx_pin && <p className="text-[11px] text-muted-foreground px-3 py-1.5 bg-card">Approximate location — this provider serves an area rather than a single storefront.</p>}
        </div>
      )}
      {(p.gallery_urls || []).length > 0 && (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2">{p.gallery_urls.map((g, i) => <img key={i} src={g} alt={`${p.name} photo ${i + 1}`} className="rounded-xl object-cover h-36 w-full" />)}</div>
      )}
      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <Button variant="outline" className="rounded-full" onClick={() => { track("info_request", { provider_id: p.id }); window.location.href = p.email ? `mailto:${p.email}?subject=${encodeURIComponent("Info request via TruckTrackr")}` : (p.website || "#"); }} data-testid="resource-request-info-btn">Request information</Button>
        <button className="text-xs text-muted-foreground hover:text-foreground underline" onClick={() => setShowReport(true)} data-testid="resource-report-btn">Report incorrect information</button>
      </div>
      {showReport && (
        <FormModal title="Report incorrect information" testid="resource-report-modal" submitLabel="Send report" onClose={() => setShowReport(false)}
          fields={[{ key: "message", label: "What's incorrect?", type: "textarea", required: true }]}
          onSubmit={async (v) => { await api.post("/resources/report", { provider_id: p.id, message: v.message }); toast.success("Thanks — we'll review it."); }} />
      )}
    </div>
  );
}

/* ---------------- directory ---------------- */

const INTRO = "Running a food truck takes more than great food. Find trusted businesses that can help with equipment, inspections, insurance, supplies, marketing, repairs, financing, and more.";

function Directory({ status, isAdmin, initialCategory }) {
  const preview = status !== "live";
  useNoIndex(preview);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState(initialCategory || null);
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const [pos, setPos] = useState(null);
  const [radius, setRadius] = useState(50);
  const [view, setView] = useState("split");
  const [mobileMap, setMobileMap] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const [showPartner, setShowPartner] = useState(false);
  const [showAllCats, setShowAllCats] = useState(false);
  const [nearFooter, setNearFooter] = useState(false);
  const listRefs = useRef({});
  const navigate = useNavigate();
  const hasQuery = Boolean(cat || q.trim() || pos);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      setNearFooter(window.innerHeight + window.scrollY >= doc.scrollHeight - 220);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { track("directory_impression"); }, []);
  useEffect(() => { api.get("/resources/categories").then(({ data }) => setCats(data.categories)).catch(() => {}); }, []);
  useEffect(() => {
    const params = new URLSearchParams();
    if (cat) params.set("category", cat.slug || cat);
    if (q) params.set("q", q);
    if (pos) { params.set("lat", pos.lat); params.set("lng", pos.lng); params.set("radius_mi", radius); }
    const t = setTimeout(() => {
      api.get(`/resources/providers?${params}`).then(({ data }) => setData(data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [cat, q, pos, radius]);
  useEffect(() => { if (cat) track("category_view", { category_id: cat.id, market: loc || null }); }, [cat]); // eslint-disable-line

  const geocode = async () => {
    if (!loc.trim()) { setPos(null); return; }
    try {
      const { data } = await api.get("/geocode", { params: { q: loc } });
      if (data.found) { setPos({ lat: data.lat, lng: data.lng }); toast.success(`Searching near ${data.display_name.split(",").slice(0, 2).join(",")}`); }
      else toast.error("Location not found");
    } catch { toast.error("Location lookup failed"); }
  };
  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available on this device"); return; }
    navigator.geolocation.getCurrentPosition(
      (g) => { setPos({ lat: g.coords.latitude, lng: g.coords.longitude }); setLoc("My location"); },
      () => toast.error("Location permission unavailable"));
  };

  const providers = data?.providers || [];
  const pinned = providers.filter((p) => p.lat != null);
  const center = pos ? [pos.lat, pos.lng] : pinned.length ? [pinned[0].lat, pinned[0].lng] : [40.04, -76.3];
  const sponsors = data?.sponsors || {};

  // Lightweight clustering: group pins within ~1.2mi of each other.
  const clusters = useMemo(() => {
    const groups = [];
    for (const p of pinned) {
      const g = groups.find((x) => Math.abs(x.lat - p.lat) < 0.018 && Math.abs(x.lng - p.lng) < 0.022);
      if (g) { g.items.push(p); } else { groups.push({ lat: p.lat, lng: p.lng, items: [p] }); }
    }
    return groups;
  }, [pinned]);
  const selectedProvider = providers.find((p) => p.id === selected);

  const card = (p) => {
    const [lvlLabel, lvlCls] = (p.sponsor_active && LEVEL_BADGE[p.listing_level]) || [];
    return (
      <div
        key={p.id}
        ref={(el) => { listRefs.current[p.id] = el; }}
        className={`rounded-2xl border bg-card p-4 transition-colors cursor-pointer ${selected === p.id ? "border-primary ring-1 ring-primary/40" : "hover:border-primary/40"} ${p.sponsor_active && p.listing_level !== "standard" ? "border-amber-300/70" : ""}`}
        onClick={() => setSelected(p.id)}
        data-testid={`resource-card-${p.slug}`}
      >
        <div className="flex items-start gap-3">
          {p.logo_url ? <img src={p.logo_url} alt="" className="h-12 w-12 rounded-xl object-cover border flex-none" />
            : <div className="h-12 w-12 rounded-xl bg-primary/10 grid place-items-center flex-none"><Toolbox weight="duotone" className="h-6 w-6 text-primary" /></div>}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{p.name}</span>
              {lvlLabel && <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${lvlCls}`}>{lvlLabel}</span>}
              {(p.badges || []).includes("Verified") && <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-green-100 text-green-800">Verified</span>}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{p.short_description}</p>
            <div className="text-xs text-muted-foreground mt-1.5 flex flex-wrap gap-x-3">
              {(p.city || p.state) && <span>{[p.city, p.state].filter(Boolean).join(", ")}</span>}
              {p.service_area_label && <span>Serves {p.service_area_label}</span>}
              {p.distance_mi != null && <span className="font-semibold text-foreground">{p.distance_mi} mi</span>}
              {p.offer && <span className="text-amber-700 font-semibold">Offer available</span>}
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {p.phone && <a href={`tel:${p.phone}`} onClick={(e) => { e.stopPropagation(); track("phone_click", { provider_id: p.id }); }} className="text-xs rounded-full border px-3 py-1.5 font-medium hover:border-primary/50">Call</a>}
          {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); track("website_click", { provider_id: p.id }); }} className="text-xs rounded-full border px-3 py-1.5 font-medium hover:border-primary/50">Website</a>}
          {p.email && <a href={`mailto:${p.email}`} onClick={(e) => { e.stopPropagation(); track("contact_click", { provider_id: p.id }); }} className="text-xs rounded-full border px-3 py-1.5 font-medium hover:border-primary/50">Contact</a>}
          <Link to={`/resources/${p.slug}`} onClick={(e) => e.stopPropagation()} className="text-xs rounded-full bg-primary text-primary-foreground px-3 py-1.5 font-semibold ml-auto" data-testid={`resource-view-${p.slug}`}>View details</Link>
        </div>
      </div>
    );
  };

  const mapEl = (
    <div className="relative h-full w-full">
      <MapContainer center={center} zoom={pos ? 9 : 8} style={{ height: "100%", width: "100%" }} data-testid="resources-map">
        <GlBasemap />
        <FlyToSelected target={selectedProvider} />
        {clusters.map((g, gi) => g.items.length > 1 ? (
          <Marker key={`c-${gi}`} position={[g.lat, g.lng]} icon={clusterIcon(g.items.length)}
            eventHandlers={{ click: (e) => e.target._map.flyTo([g.lat, g.lng], Math.min((e.target._map.getZoom() || 9) + 3, 15)) }} />
        ) : (
          (() => { const p = g.items[0]; return (
            <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(p.sponsor_active && p.listing_level !== "standard")}
              eventHandlers={{ click: () => { setSelected(p.id); track("pin_click", { provider_id: p.id }); listRefs.current[p.id]?.scrollIntoView({ behavior: "smooth", block: "nearest" }); } }}>
              <Popup>
                <div style={{ minWidth: 180 }}>
                  <div style={{ fontWeight: 700 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: "#666" }}>{p.short_description?.slice(0, 90)}</div>
                  {p.approx_pin && <div style={{ fontSize: 11, color: "#999" }}>Approximate service-area location</div>}
                  {p.distance_mi != null && <div style={{ fontSize: 12, marginTop: 2 }}>{p.distance_mi} mi away</div>}
                  <Link to={`/resources/${p.slug}`} style={{ fontSize: 12, fontWeight: 700, color: "#ea580c" }}>View resource →</Link>
                </div>
              </Popup>
            </Marker>
          ); })()
        ))}
      </MapContainer>
      {!pinned.length && (
        <div className="absolute inset-0 z-[1000] grid place-items-center pointer-events-none">
          <div className="bg-card/95 border rounded-xl px-5 py-3.5 text-center text-sm shadow-lg" data-testid="resources-map-empty">
            <p className="font-semibold">{hasQuery ? "No resources found" : "Find the right resource"}</p>
            <p className="text-muted-foreground text-xs mt-0.5">{hasQuery ? "Try expanding the distance or another category." : "Pick a category or search to see providers here."}</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-6" data-testid="resources-directory">
      <PageMeta title="Food Truck Resources — Trusted Providers | TruckTrackr" description={INTRO} canonical="https://thetrucktrackr.com/resources" />
      {preview && (
        <div className="mb-4 rounded-xl border border-violet-300 bg-violet-50 text-violet-900 px-4 py-2.5 text-sm font-medium" data-testid="resources-admin-preview-banner">
          Admin preview — the public still sees "Coming Soon". Demo listings are visible only here.
        </div>
      )}
      <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight flex items-center gap-3"><Toolbox weight="duotone" className="h-9 w-9 text-primary" /> Food Truck Resources</h1>
      <p className="mt-2 text-muted-foreground max-w-3xl">{INTRO}</p>
      <p className="mt-1 text-sm text-muted-foreground">Browse by category, search by location, or explore nearby providers on the map.</p>

      {sponsors.presenting && (
        <div className="mt-4 rounded-xl border bg-card px-4 py-2.5 text-sm flex items-center gap-2" data-testid="presenting-sponsor-banner">
          {sponsors.presenting.logo_url && <img src={sponsors.presenting.logo_url} alt="" className="h-6 w-6 rounded" />}
          <span className="text-muted-foreground">Food Truck Resources presented by</span>
          <Link to={`/resources/${sponsors.presenting.slug}`} className="font-semibold text-primary hover:underline">{sponsors.presenting.name}</Link>
        </div>
      )}

      {/* controls — clean row hierarchy on mobile, combined on desktop */}
      <div className="mt-5 space-y-2.5">
        <div className="flex flex-col lg:flex-row gap-2.5">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search resources…" className="w-full h-11 rounded-full border bg-background pl-10 pr-3 text-sm" data-testid="resources-search-input" />
          </div>
          <div className="flex gap-2">
            <input value={loc} onChange={(e) => setLoc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && geocode()} placeholder="City or ZIP" className="h-11 flex-1 lg:w-40 rounded-full border bg-background px-4 text-sm min-w-0" data-testid="resources-location-input" />
            <Button variant="outline" className="rounded-full h-11 px-5 flex-none" onClick={geocode} data-testid="resources-location-go">Go</Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full h-11 flex-1 lg:flex-none gap-1.5" onClick={useMyLocation} data-testid="resources-use-location"><MapPin className="h-4 w-4" />Near Me</Button>
            <select value={radius} onChange={(e) => setRadius(Number(e.target.value))} className="h-11 flex-1 lg:flex-none rounded-full border bg-background px-4 text-sm" data-testid="resources-distance-select">
              {[10, 25, 50, 100, 250].map((r) => <option key={r} value={r}>{r} mi</option>)}
            </select>
            <div className="hidden lg:flex rounded-full border overflow-hidden" data-testid="resources-view-toggle">
              {["list", "split", "map"].map((v) => (
                <button key={v} onClick={() => setView(v)} className={`px-4 h-11 text-sm font-medium capitalize ${view === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`resources-view-${v}`}>{v}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 lg:justify-end">
          <Button variant="outline" className="rounded-full h-11 flex-1 lg:flex-none" onClick={() => setShowSuggest(true)} data-testid="resources-suggest-btn">Suggest a Resource</Button>
          <Button className="rounded-full h-11 flex-1 lg:flex-none bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => setShowPartner(true)} data-testid="resources-partner-btn">Become a Partner</Button>
        </div>
      </div>

      {/* categories — collapsed to 8 on mobile */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 items-stretch" data-testid="resources-categories">
        {cats.map((c, i) => (
          <button key={c.id} onClick={() => { const next = cat?.id === c.id ? null : c; setCat(next); navigate(next ? `/resources/${c.slug}` : "/resources", { replace: true }); }}
            className={`rounded-xl border p-3 text-left transition-colors min-h-[86px] flex flex-col ${cat?.id === c.id ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "hover:border-primary/40"} ${i >= 8 && !showAllCats ? "hidden lg:flex" : ""}`}
            data-testid={`resource-category-${c.slug}`}>
            <CatIcon name={c.icon} className="h-5 w-5 text-primary flex-none" />
            <div className="mt-1.5 text-[13px] font-semibold leading-tight break-words">{c.name}</div>
            <div className="mt-auto pt-0.5 text-[11px] text-muted-foreground">
              {c.provider_count > 0 ? `${c.provider_count} provider${c.provider_count !== 1 ? "s" : ""}` : <span className="opacity-60">Coming soon</span>}
            </div>
          </button>
        ))}
      </div>
      {cats.length > 8 && (
        <div className="mt-2.5 lg:hidden">
          <Button variant="outline" className="rounded-full w-full h-10" onClick={() => setShowAllCats((s) => !s)} data-testid="resources-toggle-categories-btn">
            {showAllCats ? "Show Fewer Categories" : `View All Categories (${cats.length})`}
          </Button>
        </div>
      )}

      {sponsors.category && (
        <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-2.5 text-sm flex items-center gap-2" data-testid="category-sponsor-banner">
          {sponsors.category.logo_url && <img src={sponsors.category.logo_url} alt="" className="h-6 w-6 rounded" />}
          <span className="text-muted-foreground">This category is sponsored by</span>
          <Link to={`/resources/${sponsors.category.slug}`} className="font-semibold text-violet-700 hover:underline">{sponsors.category.name}</Link>
        </div>
      )}
      {sponsors.regional && (
        <div className="mt-2 rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-2.5 text-sm" data-testid="regional-sponsor-banner">
          <span className="text-muted-foreground">Your area{sponsors.regional.region ? ` (${sponsors.regional.region})` : ""} is sponsored by </span>
          <Link to={`/resources/${sponsors.regional.slug}`} className="font-semibold text-violet-700 hover:underline">{sponsors.regional.name}</Link>
        </div>
      )}

      {/* results + map */}
      <div className={`mt-5 grid gap-5 ${view === "split" ? "lg:grid-cols-[1fr_420px]" : ""}`}>
        {(view !== "map") && (
          <div className="space-y-3 pb-24 lg:pb-0" data-testid="resources-results">
            {!data ? <div className="grid place-items-center py-16"><CircleNotch className="h-7 w-7 animate-spin text-primary" /></div>
              : providers.length ? providers.map(card)
                : !hasQuery ? (
                  <div className="rounded-2xl border border-dashed p-10 text-center" data-testid="resources-initial-state">
                    <MagnifyingGlass className="h-7 w-7 text-primary mx-auto" />
                    <p className="mt-3 font-semibold">Find the right resource</p>
                    <p className="text-sm text-muted-foreground mt-1">Choose a category above or search by business name, service, city, or ZIP code.</p>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed p-10 text-center" data-testid="resources-empty-state">
                    <p className="font-semibold">No resources found</p>
                    <p className="text-sm text-muted-foreground mt-1">Try expanding the search distance, choosing another category, or suggesting a business.</p>
                    <div className="mt-4 flex flex-wrap gap-2 justify-center">
                      <Button variant="outline" className="rounded-full" onClick={() => { setCat(null); setQ(""); setLoc(""); setPos(null); navigate("/resources", { replace: true }); }} data-testid="resources-clear-filters-btn">Clear Filters</Button>
                      <Button variant="outline" className="rounded-full" onClick={() => setShowSuggest(true)}>Suggest a Resource</Button>
                    </div>
                  </div>
                )}
          </div>
        )}
        {(view === "split" || view === "map") && (
          <div className={`hidden lg:block rounded-2xl overflow-hidden border ${view === "map" ? "h-[70vh]" : "h-[60vh] sticky top-20"}`}>{mapEl}</div>
        )}
      </div>

      {/* mobile map — hidden when open, near footer, or nothing to search yet */}
      {!mobileMap && !nearFooter && (
        <Button
          className="lg:hidden fixed right-5 z-[1100] rounded-full shadow-lg gap-1.5 h-12 px-5 bg-primary text-primary-foreground"
          style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}
          onClick={() => setMobileMap(true)}
          data-testid="resources-view-map-btn"
        >
          <MapPin className="h-4 w-4" /> View Map
        </Button>
      )}
      {mobileMap && (
        <div className="lg:hidden fixed inset-0 z-[1150] bg-background flex flex-col" data-testid="resources-mobile-map">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <span className="font-semibold">Resource map</span>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => setMobileMap(false)} data-testid="resources-mobile-map-close">Back to list</Button>
          </div>
          <div className="flex-1">{mapEl}</div>
        </div>
      )}

      {showSuggest && <SuggestModal onClose={() => setShowSuggest(false)} />}
      {showPartner && <PartnerModal onClose={() => setShowPartner(false)} />}
    </div>
  );
}

/* ---------------- coming soon ---------------- */

function ComingSoon({ isAdmin, onPreview }) {
  useNoIndex(true);
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 text-center" data-testid="resources-coming-soon">
      <PageMeta title="Food Truck Resources — Coming Soon | TruckTrackr" description="TruckTrackr is building a trusted directory of businesses that help food-truck owners." />
      <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 grid place-items-center"><Toolbox weight="duotone" className="h-7 w-7 text-primary" /></div>
      <h1 className="mt-4 font-display font-bold text-3xl sm:text-4xl tracking-tight">Food Truck Resources</h1>
      <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
        Running a food truck takes more than great food. TruckTrackr is building a trusted directory of businesses
        that can help with equipment, inspections, insurance, supplies, marketing, repairs, financing, and more.
      </p>
      <div className="mt-8 rounded-3xl border-2 border-primary/30 bg-primary/5 px-8 py-10" data-testid="resources-coming-soon-banner">
        <div className="font-display font-bold text-2xl text-primary uppercase tracking-wide">Coming Soon</div>
        <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto">
          We're assembling trusted resources to help food-truck owners spend less time searching and more time
          running their businesses.
        </p>
      </div>
      {isAdmin && (
        <Button variant="outline" className="mt-6 rounded-full" onClick={onPreview} data-testid="resources-preview-live-btn">
          Preview Live Directory (admin)
        </Button>
      )}
    </div>
  );
}

/* ---------------- entry ---------------- */

export default function Resources() {
  const { slug } = useParams();
  const [state, setState] = useState(null); // {status, is_admin}
  const [resolved, setResolved] = useState(null);
  const [adminPreview, setAdminPreview] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { api.get("/resources/status").then(({ data }) => setState(data)).catch(() => setState({ status: "coming_soon", is_admin: false })); }, []);
  const showDirectory = state && (state.status === "live" || (state.is_admin && adminPreview));

  useEffect(() => {
    setResolved(null); setErr(null);
    if (!slug || !showDirectory) return;
    api.get(`/resources/resolve/${slug}`).then(({ data }) => setResolved(data)).catch((e) => setErr(e?.response?.status || 500));
  }, [slug, showDirectory]);

  if (!state) return <div className="grid place-items-center py-32"><CircleNotch className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!showDirectory) return <ComingSoon isAdmin={state.is_admin} onPreview={() => setAdminPreview(true)} />;
  if (slug) {
    if (err) return <div className="py-24 text-center text-muted-foreground" data-testid="resource-not-found">Resource not found. <Link className="text-primary underline" to="/resources">Browse all resources</Link></div>;
    if (!resolved) return <div className="grid place-items-center py-32"><CircleNotch className="h-8 w-8 animate-spin text-primary" /></div>;
    if (resolved.type === "provider") return <ProviderDetail provider={resolved.provider} indexable={resolved.indexable} />;
    return <Directory status={adminPreview ? "preview" : state.status} isAdmin={state.is_admin} initialCategory={resolved.category} />;
  }
  return <Directory status={adminPreview ? "preview" : state.status} isAdmin={state.is_admin} />;
}
